import React from 'react';
import { Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import { monthlyBenefits, benefitVisuals } from './constants';
import { getUsedBenefitsThisMonth } from './utils';
import type { MembershipPlan, Redemption } from './types';
import PremiumBenefitCard from './PremiumBenefitCard';

interface BenefitsGridProps {
  plan: MembershipPlan;
  redemptions: Redemption[];
  isAdmin?: boolean;
  onQuickRedeem?: (benefitKey: string) => void;
  layout?: 'grid' | 'list';
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export const BenefitsGrid: React.FC<BenefitsGridProps> = ({
  plan,
  redemptions,
  isAdmin = false,
  onQuickRedeem,
  layout = 'grid',
}) => {
  const benefits = monthlyBenefits[plan];

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30"
          >
            <Gift className="w-5 h-5 text-white" />
          </motion.div>
          <div>
            <h3 className="text-white font-bold text-lg">Canjes</h3>
          </div>
        </div>
        {isAdmin && (
          <span className="text-xs text-blue-400/70 bg-blue-500/10 px-3 py-1 rounded-full">
            Click en slot para canjear
          </span>
        )}
      </div>
      
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className={layout === 'grid' 
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
          : 'grid grid-cols-1 gap-4'
        }
      >
        {Object.entries(benefits).map(([key, benefit]) => {
          const used = getUsedBenefitsThisMonth(redemptions, key);
          const visual = benefitVisuals[key] || {
            icon: 'Shield',
            iconBg: 'from-gray-500 to-gray-600',
            imageBg: 'from-gray-600 to-gray-800',
            description: 'Beneficio de membresía',
          };
          
          return (
            <motion.div key={key} variants={itemVariants}>
              <PremiumBenefitCard
                benefitKey={key}
                name={benefit.name}
                maxQuantity={benefit.max}
                usedQuantity={used}
                description={visual.description}
                icon={visual.icon}
                iconBg={visual.iconBg}
                redemptions={redemptions}
                isAdmin={isAdmin}
                onRedeem={onQuickRedeem}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default BenefitsGrid;
